import React, { memo } from "react";
import { Banknote, DollarSign } from "lucide-react";
import { Product } from "@/types/product";

interface CustomerPaymentStatsProps {
  customerProducts: Product[];
  activeTab: string;
}

export const CustomerPaymentStats = memo(function CustomerPaymentStats({
  customerProducts,
  activeTab,
}: CustomerPaymentStatsProps) {
  const iqdProducts = customerProducts.filter((p) => p.currency === "IQD");
  const totalSaleIQD = iqdProducts.reduce((sum, p) => sum + p.salePrice, 0);
  const totalPaidIQD = iqdProducts.reduce((sum, p) => sum + p.paidAmount, 0);
  const totalRemainingIQD = iqdProducts.reduce((sum, p) => sum + p.remainingAmount, 0);
  const totalMonthlyIQD = iqdProducts.filter(p => p.remainingAmount > 0).reduce((sum, p) => sum + Math.min(p.monthlyInstallment, p.remainingAmount), 0);

  const usdProducts = customerProducts.filter((p) => p.currency === "USD");
  const totalSaleUSD = usdProducts.reduce((sum, p) => sum + p.salePrice, 0);
  const totalPaidUSD = usdProducts.reduce((sum, p) => sum + p.paidAmount, 0);
  const totalRemainingUSD = usdProducts.reduce((sum, p) => sum + p.remainingAmount, 0);
  const totalMonthlyUSD = usdProducts.filter(p => p.remainingAmount > 0).reduce((sum, p) => sum + Math.min(p.monthlyInstallment, p.remainingAmount), 0);

  return (
    <div className="grid grid-cols-2 gap-3">
      {/* IQD Card */}
      <div className="rounded-xl border bg-card overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 bg-amber-500/5 border-b">
          <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
            <Banknote className="h-4 w-4 text-amber-600" />
          </div>
          <span className="font-semibold">دینار</span>
        </div>
        {/* Mobile */}
        <div className="md:hidden space-y-2 text-sm p-4">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{activeTab === "payment" ? "قیست" : "فرۆشتن"}</span>
            <span className="font-medium flex items-center gap-1">
              <span dir="ltr">{(activeTab === "payment" ? totalMonthlyIQD : totalSaleIQD).toLocaleString()}</span>
              <span className="text-xs text-muted-foreground">د.ع</span>
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">وەرگیراو</span>
            <span className="font-medium text-green-600 flex items-center gap-1">
              <span dir="ltr">{totalPaidIQD.toLocaleString()}</span>
              <span className="text-xs text-muted-foreground">د.ع</span>
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">ماوە</span>
            <span className="font-medium text-red-500 flex items-center gap-1">
              <span dir="ltr">{totalRemainingIQD.toLocaleString()}</span>
              <span className="text-xs text-muted-foreground">د.ع</span>
            </span>
          </div>
        </div>
        {/* Desktop */}
        <div className="hidden md:grid grid-cols-3 divide-x divide-x-reverse">
          <div className="flex flex-col items-center justify-center px-4 py-3 hover:bg-muted/30 transition-colors">
            <span className="text-xs text-muted-foreground mb-1.5">{activeTab === "payment" ? "قیست" : "فرۆشتن"}</span>
            <span className="font-semibold text-base flex items-center gap-1">
              <span dir="ltr">{(activeTab === "payment" ? totalMonthlyIQD : totalSaleIQD).toLocaleString()}</span>
              <span className="text-xs font-normal text-muted-foreground">د.ع</span>
            </span>
          </div>
          <div className="flex flex-col items-center justify-center px-4 py-3 hover:bg-muted/30 transition-colors">
            <span className="text-xs text-muted-foreground mb-1.5">وەرگیراو</span>
            <span className="font-semibold text-base text-green-600 flex items-center gap-1">
              <span dir="ltr">{totalPaidIQD.toLocaleString()}</span>
              <span className="text-xs font-normal text-muted-foreground">د.ع</span>
            </span>
          </div>
          <div className="flex flex-col items-center justify-center px-4 py-3 hover:bg-muted/30 transition-colors">
            <span className="text-xs text-muted-foreground mb-1.5">ماوە</span>
            <span className="font-semibold text-base text-red-500 flex items-center gap-1">
              <span dir="ltr">{totalRemainingIQD.toLocaleString()}</span>
              <span className="text-xs font-normal text-muted-foreground">د.ع</span>
            </span>
          </div>
        </div>
      </div>

      {/* USD Card */}
      <div className="rounded-xl border bg-card overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 bg-emerald-500/5 border-b">
          <div className="h-8 w-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <DollarSign className="h-4 w-4 text-emerald-600" />
          </div>
          <span className="font-semibold">دۆلار</span>
        </div>
        {/* Mobile */}
        <div className="md:hidden space-y-2 text-sm p-4">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{activeTab === "payment" ? "قیست" : "فرۆشتن"}</span>
            <span className="font-medium" dir="ltr">${(activeTab === "payment" ? totalMonthlyUSD : totalSaleUSD).toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">وەرگیراو</span>
            <span className="font-medium text-green-600" dir="ltr">${totalPaidUSD.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">ماوە</span>
            <span className="font-medium text-red-500" dir="ltr">${totalRemainingUSD.toLocaleString()}</span>
          </div>
        </div>
        {/* Desktop */}
        <div className="hidden md:grid grid-cols-3 divide-x divide-x-reverse">
          <div className="flex flex-col items-center justify-center px-4 py-3 hover:bg-muted/30 transition-colors">
            <span className="text-xs text-muted-foreground mb-1.5">{activeTab === "payment" ? "قیست" : "فرۆشتن"}</span>
            <span className="font-semibold text-base" dir="ltr">${(activeTab === "payment" ? totalMonthlyUSD : totalSaleUSD).toLocaleString()}</span>
          </div>
          <div className="flex flex-col items-center justify-center px-4 py-3 hover:bg-muted/30 transition-colors">
            <span className="text-xs text-muted-foreground mb-1.5">وەرگیراو</span>
            <span className="font-semibold text-base text-green-600" dir="ltr">${totalPaidUSD.toLocaleString()}</span>
          </div>
          <div className="flex flex-col items-center justify-center px-4 py-3 hover:bg-muted/30 transition-colors">
            <span className="text-xs text-muted-foreground mb-1.5">ماوە</span>
            <span className="font-semibold text-base text-red-500" dir="ltr">${totalRemainingUSD.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
});
